import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AiRequestUtilsService } from 'src/app/api/services/ai-request-utils.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { AIChatbotMessageContent } from '../../models/ai-chatbot-message';
import { AiChatbotTemplateTunerGeneration } from '../../models/ai-chatbot-presentation-data';
import { AIChatbotInterpretationResponse } from '../../models/ai-chatbot-interpretation-response';
import { AIChatbotSession } from '../../models/ai-chatbot-session';
import { parseSSEStream } from './sse-stream-reader';

export type SelectedTemplateTunerContext = {
  messageId: string;
  optionIndex: number;
  presentation: AiChatbotTemplateTunerGeneration;
};

@Injectable({ providedIn: 'root' })
export class AIChatbotApiService {
  constructor(
    private userStateService: UserStateService,
    private aiRequestUtils: AiRequestUtilsService,
  ) {}

  async interpret(
    session: AIChatbotSession,
    message: string,
    content: AIChatbotMessageContent,
    cancellationSubject: Subject<void>,
    selectedTemplateTuner?: SelectedTemplateTunerContext | null,
    templateEditHistory?: { html: string; userMessage: string }[],
    onLoadingEvent?: (event: { data: string; message: string }) => void,
    onPartialEvent?: (
      partial: AiChatbotTemplateTunerGeneration,
      generationIndex: number,
    ) => void,
    mode?: 'fast' | 'quality',
  ): Promise<AIChatbotInterpretationResponse> {
    const { url, headers, body } = this.getChatApiCallParameters(
      session,
      message,
      content,
      selectedTemplateTuner,
      templateEditHistory,
      mode,
    );

    const abortController = new AbortController();
    // Subscribe to the cancellation subject to abort the fetch request if needed
    const cancellationSub = cancellationSubject.subscribe(() =>
      abortController.abort(),
    );

    try {
      // Convert HttpHeaders to a plain object for the fetch API
      const fetchHeaders = Object.fromEntries(
        headers.keys().map((key) => [key, headers.get(key)!]),
      );

      // Use the native fetch API to support streaming responses (SSE)
      const response = await fetch(url, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify(body),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      if (!response.body) throw new Error('Response body is null');

      // Process the Server-Sent Events (SSE) stream
      return await this.readSSEStream(
        response.body,
        onLoadingEvent,
        onPartialEvent,
      );
    } catch (error: any) {
      if (error?.name === 'AbortError') throw 'processing_canceled';
      throw error;
    } finally {
      cancellationSub.unsubscribe();
    }
  }

  private async readSSEStream(
    body: ReadableStream<Uint8Array>,
    onLoadingEvent?: (event: { data: string; message: string }) => void,
    onPartialEvent?: (
      partial: AiChatbotTemplateTunerGeneration,
      generationIndex: number,
    ) => void,
  ): Promise<AIChatbotInterpretationResponse> {
    let result: AIChatbotInterpretationResponse | null = null;

    for await (const event of parseSSEStream(body)) {
      if (event === null) {
        result = result ?? { type: 'chat' };
        break;
      }

      if (event.type === 'loading') {
        onLoadingEvent?.({
          data: event.data as string,
          message: event.message as string,
        });
      } else if (event.type === 'template_tuner_partial') {
        onPartialEvent?.(
          event.data as AiChatbotTemplateTunerGeneration,
          event.generationIndex as number,
        );
      } else if (event.type === 'error') {
        throw new Error(
          (event.data as string) ?? 'An error occurred. Please try again.',
        );
      } else {
        result = event as unknown as AIChatbotInterpretationResponse;
      }
    }

    if (result === null) throw new Error('No result received from stream');
    return result;
  }

  getApiCallParameters(endpoint: string, sessionId: string): any {
    return this.aiRequestUtils.buildCall(`api/${endpoint}`, { sessionId });
  }

  getChatApiCallParameters(
    session: AIChatbotSession,
    message: string,
    content: AIChatbotMessageContent,
    selectedTemplateTuner?: SelectedTemplateTunerContext | null,
    templateEditHistory?: { html: string; userMessage: string }[],
    mode?: 'fast' | 'quality',
  ) {
    const callParams = this.getApiCallParameters('v4/chat', session.id);

    const history = session.messages();
    const cleanHistory = history.filter((msg) => msg.role !== 'error');

    const payload = {
      email: this.userStateService.getUserEmail(),
    };

    callParams.body.message = message;
    callParams.body.history = cleanHistory.slice(0, -1);
    callParams.body.payload = payload;
    if (mode) {
      callParams.body.mode = mode;
    }

    if (selectedTemplateTuner) {
      callParams.body.selectedTemplateTuner = {
        messageId: selectedTemplateTuner.messageId,
        optionIndex: selectedTemplateTuner.optionIndex,
        prompt: selectedTemplateTuner.presentation.prompt,
        style: selectedTemplateTuner.presentation.style,
        rawContentUrl: selectedTemplateTuner.presentation.rawContentUrl,
      };

      if (templateEditHistory && templateEditHistory.length > 0) {
        callParams.body.templateEditHistory = templateEditHistory;
      }
    }

    return callParams;
  }
}
